import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearFilterInquiry, setFilterInquiry } from "./inquirySlice";
import { getAllInquiry } from "./services/inquiry.services";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { Tooltip } from "@progress/kendo-react-tooltip";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import ExpandableText from "../../components/common/ExpandableText";

const Inquiry: React.FC = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
    };
  }, []);
  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Inquiry List
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <InquiryGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const InquiryGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const InquiryList = useAppSelector((state) => state.inquiry.InquiryList);
  const loading = useAppSelector((state) => state.inquiry.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterInquiry = useAppSelector((state) => state.inquiry.FilterInquiry);
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  useEffect(() => {
    const payload = {
      ...FilterInquiry,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllInquiry(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 3 || props.index === 4 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const InquiryFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.inquiry.FilterInquiry}
        setFilterAction={setFilterInquiry}
        clearFilterAction={clearFilterInquiry}
        fetchDataAction={getAllInquiry}
        pagination={{ case: "capitalize" }}
      />
    ),
    [],
  );

  // if (loading) return ;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        className="responsive-table"
        filter={filter}
        data={filter ? filterBy(InquiryList, filter) : InquiryList}
        onFilterChange={handleFilterChange}
        total={InquiryList[0]?.itemcount || InquiryList.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="Inquirydate"
          title="Inquiry Date"
          width={120}
          filterable={false}
        />
        <Column
          field="FirstName"
          title="Full Name"
          width={250}
          cells={{
            data: (props) => (
              <td>
                <div>
                  {props?.dataItem.FirstName} {props?.dataItem.LastName}
                </div>
              </td>
            ),
            filterCell: InquiryFilterCell,
          }}
        />
        <Column
          field="Email"
          title="Email"
          width={280}
          cells={{
            data: (props) => (
              <td>
                <div
                  title={props?.dataItem.Email}
                  style={{ cursor: "pointer" }}
                >
                  <Tooltip parentTitle={true}>{props?.dataItem.Email}</Tooltip>
                </div>
              </td>
            ),
          }}
        />
        <Column field="MobileNo" title="Mobile No" width={150} />
        <Column field="CompanyName" title="Company Name" width={200} />
        <Column field="CountryName" title="Country Name" width={200} />
        <Column
          field="BusinessType"
          title="Business Type"
          width={200}
          cells={{
            data: (props) => (
              <td>
                <div>
                  {props?.dataItem.BusinessType === "Other"
                    ? props?.dataItem?.BusinessTypeOther
                    : props?.dataItem.BusinessType}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="CurrentStage"
          title="Current Stage"
          width={200}
          cells={{
            data: (props) => (
              <td>
                <div>
                  {props?.dataItem.CurrentStage === "Other"
                    ? props?.dataItem?.CurrentStageOther
                    : props?.dataItem.CurrentStage}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="ExpectedTimeline"
          title="Expected Timeline"
          width={200}
          cells={{
            data: (props) => (
              <td>
                <div>
                  {props?.dataItem.ExpectedTimeline === "Other"
                    ? props?.dataItem?.ExpectedTimelineOther
                    : props?.dataItem.ExpectedTimeline}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="Remarks"
          title="Remarks"
          width={300}
          filterable={true}
          cells={{
            data: (props: any) => (
              <ExpandableText text={props.dataItem.Remarks} />
            ),
          }}
        />
      </KendoGrid>
    </>
  );
};

export default Inquiry;
